import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';
import { ArrowBackIcon, Box, Container, Flex, Grid, IconButton, Skeleton, Text, useToast } from '@tradener/lumen';

import CREDIT_COUNTERPARTY_DETAIL, {
  PayloadProps as CreditCounterpartyDetailPayloadProps,
  ResponseProps as CreditCounterpartyDetailResponseProps,
} from '@graphql/query/operationLimits/credit/counterpartDetail';

import CounterpartyChartLayoutSelector, { CounterpartyChartLayoutProps } from './components/CounterpartyChartLayoutSelector';
import CounterpartyMtmChart from './components/CounterpartyMtmChart';
import CounterpartyMwmChart from './components/CounterpartyMwmChart';
import CounterpartyScrollableChartCard from './components/CounterpartyScrollableChartCard';
import CounterpartySummaryCards from './components/CounterpartySummaryCards';
import { EMPTY_COUNTERPARTY_DETAIL, resolveCounterpartySubtitle, resolveRatingStyle } from './counterpartyPresentation';
import useOperationLimitsDetailFilters from './hooks/useOperationLimitsDetailFilters';

function OperationLimitsDetail() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { counterpartyId } = useParams();
  const [selectedCounterpartyName, setSelectedCounterpartyName] = useState('');
  const [hoveredCompetence, setHoveredCompetence] = useState<string>();
  const [isChartAnimationActive, setChartAnimationActive] = useState(false);
  const [isChartScrollbarVisible, setChartScrollbarVisible] = useState(false);
  const isSyncingScrollRef = useRef(false);
  const hideChartScrollbarTimeoutRef = useRef<number>();
  const mwmScrollRef = useRef<HTMLDivElement>(null);
  const mtmScrollRef = useRef<HTMLDivElement>(null);
  const { queryFilters } = useOperationLimitsDetailFilters();
  const chartLayout = searchParams.get('chartLayout') === 'vertical' ? 'vertical' : 'horizontal';

  const { data, loading, error } = useQuery<CreditCounterpartyDetailResponseProps, CreditCounterpartyDetailPayloadProps>(
    CREDIT_COUNTERPARTY_DETAIL,
    {
      skip: !counterpartyId,
      variables: {
        accountId: counterpartyId!,
        filter: queryFilters,
      },
      fetchPolicy: 'cache-and-network',
    },
  );

  const isLoaded = !loading;
  const detail = data?.creditCounterpartDetail;
  const detailForDisplay = detail || EMPTY_COUNTERPARTY_DETAIL;
  const hasSelectedCounterparty = Boolean(counterpartyId);
  const hasCounterpartyData = Boolean(detail);
  const counterpartySubtitle = resolveCounterpartySubtitle(
    hasSelectedCounterparty,
    hasCounterpartyData,
    detail?.rating,
    detail?.negotiationDeadlineLabel,
  );
  const ratingStyle = resolveRatingStyle(counterpartySubtitle.ratingLabel);
  const mwmSeries = hasSelectedCounterparty ? detail?.series?.mwmByCompetence || [] : [];
  const mtmSeries = hasSelectedCounterparty ? detail?.series?.mtmByCompetence || [] : [];
  const chartWidth = useMemo(() => {
    const seriesSize = Math.max(mwmSeries.length, mtmSeries.length, 12);

    return `max(100%, ${seriesSize * 40}px)`;
  }, [mwmSeries.length, mtmSeries.length]);

  useEffect(() => {
    if (!error) {
      return;
    }

    toast({ title: 'Erro', status: 'error', description: error.message || 'Erro ao carregar detalhe da contraparte.' });
  }, [error, toast]);

  useEffect(() => {
    if (!counterpartyId) {
      setSelectedCounterpartyName('');

      return;
    }

    if (detail?.counterpartName) {
      setSelectedCounterpartyName(detail.counterpartName);
    }
  }, [counterpartyId, detail?.counterpartName]);

  useEffect(() => {
    if (!counterpartyId) {
      setChartAnimationActive(false);

      return;
    }

    setChartAnimationActive(true);

    const timeoutId = window.setTimeout(() => {
      setChartAnimationActive(false);
    }, 700);

    return () => window.clearTimeout(timeoutId);
  }, [counterpartyId]);

  const syncChartScroll = useCallback((source: 'mwm' | 'mtm') => {
    if (isSyncingScrollRef.current) {
      return;
    }

    const sourceElement = source === 'mwm' ? mwmScrollRef.current : mtmScrollRef.current;
    const targetElement = source === 'mwm' ? mtmScrollRef.current : mwmScrollRef.current;

    if (!sourceElement || !targetElement) {
      return;
    }

    isSyncingScrollRef.current = true;
    targetElement.scrollLeft = sourceElement.scrollLeft;

    window.requestAnimationFrame(() => {
      isSyncingScrollRef.current = false;
    });
  }, []);

  const handleChartScroll = useCallback(
    (source: 'mwm' | 'mtm') => {
      setChartScrollbarVisible(true);

      syncChartScroll(source);

      if (hideChartScrollbarTimeoutRef.current) {
        window.clearTimeout(hideChartScrollbarTimeoutRef.current);
      }

      hideChartScrollbarTimeoutRef.current = window.setTimeout(() => setChartScrollbarVisible(false), 850);
    },
    [syncChartScroll],
  );

  useEffect(
    () => () => {
      if (!hideChartScrollbarTimeoutRef.current) {
        return;
      }

      window.clearTimeout(hideChartScrollbarTimeoutRef.current);
    },
    [],
  );

  const chartScrollSx = {
    scrollbarWidth: isChartScrollbarVisible ? 'thin' : 'none',
    scrollbarColor: 'var(--lumen-colors-gray-400) transparent',
    '&::-webkit-scrollbar': {
      height: isChartScrollbarVisible ? '10px' : '0px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'var(--lumen-colors-gray-400)',
      borderRadius: '999px',
    },
  };

  const handleBackToDashboard = useCallback(() => {
    navigate(`/limits/dashboard${location.search || ''}`);
  }, [location.search, navigate]);

  const handleChartLayoutChange = useCallback(
    (nextLayout: CounterpartyChartLayoutProps) => {
      const nextSearchParams = new URLSearchParams(searchParams);

      if (nextLayout === 'horizontal') {
        nextSearchParams.delete('chartLayout');
      } else {
        nextSearchParams.set('chartLayout', nextLayout);
      }

      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  return (
    <Container padding="0" maxW="unset" data-testid="credit-detail-page">
      <Flex paddingInline="5" paddingTop="3" paddingBottom="3">
        <Flex direction="column" rowGap="1" width="full">
          <Flex justifyContent="flex-start" alignItems="center" gap="2" height="8" mb="2">
            <IconButton
              aria-label="voltar"
              colorScheme="transparent"
              variant="ghost"
              width="fit-content"
              height="fit-content"
              minWidth="initial"
              boxSize="8"
              disabled={false}
              _hover={{ bgColor: 'gray.100' }}
              icon={<ArrowBackIcon boxSize="6" color="gray.900" />}
              onClick={handleBackToDashboard}
            />
            <Text textStyle="subheading1-medium">Voltar para dashboard</Text>
          </Flex>

          <Skeleton isLoaded={isLoaded} borderRadius="md" width="full">
            <Flex align="center" justify="space-between" columnGap="3" width="full">
              {hasSelectedCounterparty ? (
                <>
                  <Flex direction="column" rowGap="1">
                    <Text textStyle="h6-semibold" fontSize="2.2rem" color="gray.900" lineHeight="1">
                      {detail?.counterpartName || selectedCounterpartyName || '-'}
                    </Text>

                    <Text textStyle="body2-regular" color="gray.500" lineHeight="1">
                      {detail?.taxId ? detail.taxId.formatTaxId() : '-'}
                    </Text>
                  </Flex>

                  <Flex align="center" columnGap="2" justify="flex-end">
                    {counterpartySubtitle.meaningLabel ? (
                      <Text textStyle="h6-semibold" fontSize="1.8rem" color="gray.900" lineHeight="1">
                        {counterpartySubtitle.meaningLabel}
                      </Text>
                    ) : null}

                    <Box
                      borderRadius="sm"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      minW="3.25rem"
                      height="2.35rem"
                      px="3"
                      bg={ratingStyle.bg}
                      boxShadow={ratingStyle.shadow}
                    >
                      <Text textStyle="subheading2-semibold" color="white" lineHeight="1">
                        {counterpartySubtitle.ratingLabel}
                      </Text>
                    </Box>
                  </Flex>
                </>
              ) : null}
            </Flex>
          </Skeleton>
        </Flex>
      </Flex>

      <Grid width="full" gridTemplateColumns="repeat(4, 1fr)" gap="3" paddingInline="5" paddingTop="0" paddingBottom="3">
        {isLoaded ? (
          <CounterpartySummaryCards detail={detailForDisplay} isEmptyState={!hasSelectedCounterparty || !detail} />
        ) : (
          <>
            <Skeleton borderRadius="xl" height="5.5rem" />
            <Skeleton borderRadius="xl" height="5.5rem" />
            <Skeleton borderRadius="xl" height="5.5rem" />
            <Skeleton borderRadius="xl" height="5.5rem" />
          </>
        )}
      </Grid>

      <CounterpartyChartLayoutSelector layout={chartLayout} onChange={handleChartLayoutChange} />

      <Flex
        width="full"
        direction={chartLayout === 'vertical' ? 'column' : 'row'}
        gap="3"
        paddingInline="5"
        paddingTop="0"
        paddingBottom="3"
        flex="1"
      >
        <CounterpartyScrollableChartCard
          isLoaded={isLoaded}
          title="MWm por competência"
          chartWidth={chartWidth}
          scrollRef={mwmScrollRef}
          chartScrollSx={chartScrollSx}
          onScroll={() => handleChartScroll('mwm')}
        >
          <CounterpartyMwmChart
            series={mwmSeries}
            activeCompetence={hoveredCompetence}
            onHoverCompetence={setHoveredCompetence}
            shouldAnimate={isChartAnimationActive}
            hideHeader
          />
        </CounterpartyScrollableChartCard>

        <CounterpartyScrollableChartCard
          isLoaded={isLoaded}
          title="MtM por competência"
          chartWidth={chartWidth}
          scrollRef={mtmScrollRef}
          chartScrollSx={chartScrollSx}
          onScroll={() => handleChartScroll('mtm')}
        >
          <CounterpartyMtmChart
            series={mtmSeries}
            activeCompetence={hoveredCompetence}
            onHoverCompetence={setHoveredCompetence}
            shouldAnimate={isChartAnimationActive}
            hideHeader
          />
        </CounterpartyScrollableChartCard>
      </Flex>
    </Container>
  );
}

export default OperationLimitsDetail;
